import React, {useContext, useEffect, useState} from 'react';
import classNames from 'classnames';
import {FeedbackAnswers, IFeedbackAnswersProps} from '../feedback-answers';
import { Count } from '../count';
import { Filter, IFilterProps } from '../forms/filter';
import { DataInfo, IDataInfoProps } from '../data-info';
import { Button } from '../button';
import { Icon } from '../icon/icon';
import {IAnswerProps, IComment} from '../feedback-answers/feedback-answers';
import { Tabs, TabsItem, ITabsNavItemProps } from '../tabs';
import {StoreContext} from '../../../app/src/base/root-store';
import {isMobileDevice} from '../helpers';

export interface IFeedbackProps {
    items: IFeedbackItemProps[] | IDataInfoProps;
    aspectRatio?: boolean;
    className?: string;
    filter?: IFilterProps;
    includeLikesAndComments?: boolean;
}

export interface IFeedbackItemProps extends ITabsNavItemProps {
    answers: IFeedbackAnswersProps;
    id: string;
    title: string;
    count?: number;
    question?: string;
}

const VISIBLE_COUNT = 10;

const isFeedbackArray = (
    data: IFeedbackItemProps[] | IDataInfoProps,
): data is IFeedbackItemProps[] => Array.isArray(data);

const Feedback = ({
    items: initialItems,
    aspectRatio,
    className: customClass,
    filter,
    includeLikesAndComments,
}: IFeedbackProps): JSX.Element => {

    const store = useContext(StoreContext).teamResultsStore;
    const className = classNames('feedback', { 'feedback--aspect-ratio': aspectRatio }, customClass);

    if (!isFeedbackArray(initialItems)) {
        return <DataInfo {...initialItems} />;
    }

    const [items, setItems] = useState(initialItems);
    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
    const [visibleCount, setVisibleCount] = useState(VISIBLE_COUNT);
    const [openTab, setOpenTab] = useState<string>(items[0]?.id);

    useEffect(() => {
        setItems(initialItems);
    }, [initialItems]);

    const handleCommentAdded = (itemId: string, answerId: string, comment: IComment) => {
        setItems((prevItems) =>
            prevItems.map((item) => {
                if (item.id !== itemId) {
                    return item;
                }

                const updatedAnswers = item.answers.answers.map((ans: IAnswerProps) =>
                    ans.id !== answerId
                        ? ans
                        : {
                            ...ans,
                            comments: [...(ans.comments ?? []), comment],
                            commentCount: (ans.commentCount ?? 0) + 1,
                        },
                );

                return {
                    ...item,
                    answers: {
                        ...item.answers,
                        answers: updatedAnswers,
                    },
                };
            }),
        );
    };

    const visibleItems = items.slice(0, visibleCount);
    const hasMore = visibleCount < items.length;

    if (isMobileDevice()) {
        return (
            <div className={className}>
                <div className="feedback__inner">
                    {filter && <Filter {...filter} className="feedback__filter" hideLabel />}
                    <ul className="feedback__list">
                        {visibleItems.map((item) => {
                            const isExpanded = expandedItemId === item.id;

                            return (
                                <li
                                    key={item.id}
                                    className={classNames('feedback__item', {
                                        'feedback__item--expanded': isExpanded,
                                    })}
                                >
                                    <div className={classNames('select', { 'is-focused': isExpanded })}>
                                        <button
                                            onClick={() => setExpandedItemId(isExpanded ? null : item.id)}
                                            className="feedback__question-toggle"
                                        >
                                            <div className="feedback__label">
                                                <div className="feedback__label-inner">
                                                    <span className="feedback__title text-small">{item.title}</span>
                                                    {item.count !== undefined && (
                                                        <Count
                                                            className="feedback__count"
                                                            color={item.count > 0 ? 'red' : 'grey'}
                                                            number={item.count}
                                                            size="sm"
                                                        />
                                                    )}
                                                </div>
                                                <p className="feedback__question">{item.question}</p>
                                            </div>
                                            <Icon name="caret-down" className="select__icon" />
                                        </button>
                                    </div>

                                    {isExpanded && (
                                        <div className="feedback__answers">
                                            <FeedbackAnswers
                                                {...item.answers}
                                                onCommentAdded={(answerId, comment) =>
                                                    handleCommentAdded(item.id, answerId, comment)
                                                }
                                                includeLikesAndComments={includeLikesAndComments}
                                            />
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {hasMore && (
                        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                            <Button
                                text={store.label('dashboard.questions.load-more')}
                                color="white"
                                onClick={() => setVisibleCount((prev) => prev + VISIBLE_COUNT)}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const renderLabel = (item: IFeedbackItemProps): JSX.Element => (
        <div className="feedback__label">
            <div className="feedback__label-inner">
                <span className="feedback__title text-small">{item.title}</span>
                {item.count && (
                    <Count
                        className="feedback__count"
                        color={item.count > 0 ? 'red' : 'grey'}
                        number={item.count}
                        size="sm"
                    />
                )}
            </div>
            <p className="feedback__question">{item.question}</p>
        </div>
    );

    const tabItems: ITabsNavItemProps[] = items.map((item) => ({
        ...item,
        label: renderLabel(item),
    }));

    return (
        <div className={className}>
            <div className="feedback__inner">
                {filter && <Filter {...filter} className="feedback__filter" hideLabel />}
                <Tabs className="feedback__tabs" value={openTab} onChange={setOpenTab} items={tabItems}>
                    {items.map((item) => (
                        <TabsItem id={item.id} key={item.id} label={item.title}>
                            <FeedbackAnswers
                                {...item.answers}
                                onCommentAdded={(answerId, comment) =>
                                    handleCommentAdded(item.id, answerId, comment)
                                }
                                includeLikesAndComments={includeLikesAndComments}
                            />
                        </TabsItem>
                    ))}
                </Tabs>
            </div>
        </div>
    );
};

export default Feedback;
